import { type ReactNode, useRef, useState } from "react";
import { ApiError, api, type Item, type Note, type Person, type TripView } from "./api.ts";
import { examplePrompt } from "./ask-ai.ts";
import { firstName, inOrder } from "./faces.ts";
import { itemKind, kindClass, kindOf, listNames, TIER_LABEL, wantsLine } from "./format.ts";
import { FormerFace } from "./former.tsx";
import { InviteLink } from "./Invite.tsx";
import { useKept, useKeptScroll } from "./kept.ts";
import { NoteList } from "./Notes.tsx";
import { OfferToYou, offerUntil } from "./Offer.tsx";
import { litProps, useLastOpened } from "./opened.ts";
import { ratingWaits } from "./plan.ts";
import { ReportThis } from "./ReportThis.tsx";
import { addSpotsPath, blocksNeeded, dollars } from "./Spots.tsx";
import { Avatar, AvatarStack, Button } from "./souvenir.ts";
import { FirstVisit } from "./Tips.tsx";
import { ButtonLink, Count, Empty, FoldLine, KindIcon, SectionTitle } from "./ui.tsx";
import { useViewing } from "./viewing.ts";
import { Whose } from "./Whose.tsx";
import { byTierGiven, TIERS, type Tier, together } from "./wants.ts";

// Who's in the trip and what each of them wants: where wants meet, each person's list by tier, and
// what I haven't rated; then how people get in (the join link, requests waiting on the owner, the
// owner's decisions). When the trip is full, letting someone in starts with adding spots (they're let in
// once paid). The owner offers the trip to a member (Offer to), who takes it over by accepting it and covering it as it
// is (DESIGN.md, Member, "Taking over a trip"): the offer waits on them at the top, saying what that needs (Offer.tsx),
// and on their row for the owner, with Withdraw offer. On a read-only trip only turning down and removing people stay
// (nobody let in, no new link, no offering it, nothing to rate). Everything here also works by asking an AI. Leaving
// is in Trip settings.

type Confirm = { kind: "remove"; person: Person } | { kind: "owner"; person: Person } | { kind: "link" };

export function People({
  trip,
  items,
  all,
  notes,
  onOpen,
  onChanged,
  onStartRating,
}: {
  trip: TripView;
  // What's in the plan, with everyone's interest.
  items: Item[];
  // Everything in the trip, options too: what waits on my rating is worked out over all of it, by the one
  // rule Ideas, the tabs and Your trips count by (src/waits.ts).
  all: Item[];
  notes: Note[];
  onOpen: (id: string) => void;
  onChanged: () => void;
  // "Not rated by you"'s Start rating: the deck, closing back here.
  onStartRating: () => void;
}) {
  const owner = trip.owner === trip.you;
  // A shared trip's view (features/sharing.md): no join link and nothing waiting on anyone; Together over the people
  // whose ratings show.
  const viewing = useViewing();
  const ownerName = trip.members.find((m) => m.id === trip.owner)?.name ?? "The owner";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A let-in refused because the trip just filled up: the way to add spots for that person.
  const [filled, setFilled] = useState<{ name: string; href: string } | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  // Whom the owner has offered the trip to, while the offer waits (only the owner and that member see it).
  const offeredTo = owner && trip.offer ? trip.offer : null;
  const [link, setLink] = useState(trip.join_link);
  const list = useRef<HTMLElement>(null);
  useKeptScroll(list, "people");

  const act = async (run: () => Promise<unknown>, letting?: Person) => {
    setBusy(true);
    setError(null);
    setFilled(null);
    try {
      await run();
      setConfirm(null);
      onChanged();
    } catch (e) {
      // Refused for room (not because the trip is read-only, which is also a 402).
      if (letting && e instanceof ApiError && e.status === 402 && e.data["kind"] === "seats") {
        setFilled({ name: letting.name, href: addFor(letting) });
        onChanged();
      } else setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const member = (p: Person) => p.member ?? "";
  // Full: letting someone in starts with adding spots (an answer kept from before spots says nothing).
  // Read-only: nobody can be let in, and no spots are for sale (add_url is null), until it's covered again.
  const seats = trip.seats;
  const locked = Boolean(trip.locked);
  const full = !locked && seats?.full === true && seats.limit !== null ? { ...seats, limit: seats.limit } : null;
  const blocks = full ? blocksNeeded(full) : 1;
  const addFor = (p: Person) => addSpotsPath(trip.id, { letIn: member(p), back: `/t/${trip.id}?view=people`, blocks });

  return (
    <section className="tm-people" aria-labelledby="people-title" ref={list}>
      <h1 id="people-title" className="display-m">
        People
      </h1>
      <FirstVisit view="people" />

      <OfferToYou trip={trip} onChanged={onChanged} />

      {owner && trip.requests.length > 0 && (
        <section className="tm-people-block" aria-labelledby="asking">
          <SectionTitle id="asking" count={trip.requests.length}>
            Asking to join
          </SectionTitle>
          {locked && <p className="callout tm-muted">The trip is read-only, so nobody can be let in for now.</p>}
          {full && (
            <p className="callout tm-muted">
              The trip is full: {full.used} of {full.limit} spots used, owner included. Add {5 * blocks} more spots for{" "}
              {dollars(full.prices.block * blocks)} to let someone in; they're in once it's paid.
            </p>
          )}
          <ul className="tm-people-list">
            {trip.requests.map((p) => (
              <li key={p.id} className="tm-person">
                <Avatar name={p.name} color={p.color} {...(p.src ? { src: p.src } : {})} size={40} />
                <span className="tm-person-name body-strong">{p.name}</span>
                <span className="tm-person-actions">
                  <Button
                    variant="quiet"
                    size="sm"
                    disabled={busy}
                    onClick={() => act(() => api.remove(trip.id, member(p)))}
                  >
                    Turn down
                  </Button>
                  {locked ? null : full ? (
                    <ButtonLink variant="primary" size="sm" href={addFor(p)}>
                      Add spots to let in
                    </ButtonLink>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => act(() => api.letIn(trip.id, member(p)), p)}
                    >
                      Let in
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {filled && (
            <p className="callout tm-error" role="alert">
              The trip just filled up.{" "}
              <a className="tm-link" href={filled.href}>
                Add spots to let {filled.name} in
              </a>
            </p>
          )}
        </section>
      )}

      <div className="tm-people-grid">
        {/* On a shared trip's view, everyone's wants only with Who wants what and someone whose ratings show. */}
        {(!viewing || (viewing.shares.has("ratings") && trip.members.some((m) => !m.anonymous))) && (
          <Wants
            trip={trip}
            items={items}
            // A read-only trip takes no ratings: nothing waits on mine.
            notYet={locked || viewing ? [] : ratingWaits(all, trip.you).ideas}
            onOpen={onOpen}
            onStartRating={onStartRating}
          />
        )}
        <div className="tm-people-side">
          {!viewing && (
            <section className="tm-people-block" aria-labelledby="bring">
              <h2 id="bring" className="title">
                Bring people in
              </h2>
              {/* Read-only: nobody new can ask to join, so the link isn't offered (the server refuses a request). */}
              {locked ? (
                <p className="callout tm-muted">The trip is read-only, so nobody new can ask to join for now.</p>
              ) : (
                <>
                  <p className="callout tm-muted">
                    Anyone with this link can ask to join.{" "}
                    {owner ? "You decide who gets in." : `${ownerName} decides who gets in.`}
                  </p>
                  <InviteLink title={trip.title} link={link} />
                </>
              )}
              {owner &&
                !locked &&
                (confirm?.kind === "link" ? (
                  <ConfirmRow
                    text="The current link stops working. Anyone who hasn't used it yet will need the new one."
                    action="Make a new link"
                    busy={busy}
                    onConfirm={() => act(async () => setLink((await api.newJoinLink(trip.id)).join_link))}
                    onCancel={() => setConfirm(null)}
                  />
                ) : (
                  <Button variant="quiet" size="sm" className="tm-start" onClick={() => setConfirm({ kind: "link" })}>
                    Make a new link
                  </Button>
                ))}
            </section>
          )}

          <section className="tm-people-block" aria-labelledby="in-trip">
            <SectionTitle id="in-trip" count={trip.members.length}>
              In the trip
            </SectionTitle>
            <ul className="tm-people-list">
              {trip.members.map((p) => {
                const you = p.id === trip.you;
                const pending = confirm && confirm.kind !== "link" && confirm.person.id === p.id ? confirm : null;
                return (
                  <li key={p.id} className="tm-person">
                    <Avatar name={p.name} color={p.color} {...(p.src ? { src: p.src } : {})} size={40} />
                    <span className="tm-person-name">
                      <span className="body-strong">{you ? `${p.name} (you)` : p.name}</span>
                      {p.id === trip.owner && <span className="caption tm-muted">Owner</span>}
                      {p.id === offeredTo?.to && (
                        <span className="caption tm-muted">Offered the trip, until {offerUntil(offeredTo.until)}</span>
                      )}
                    </span>
                    {!pending && (
                      <span className="tm-person-actions">
                        {owner && !you && (
                          <>
                            {/* Offering the trip is a write a read-only trip refuses; removing someone isn't. Never to
                                one of the sample trip's people, who never sign in. */}
                            {!locked &&
                              !p.sample &&
                              (p.id === offeredTo?.to ? (
                                <Button
                                  variant="quiet"
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => act(() => api.withdrawOffer(trip.id))}
                                >
                                  Withdraw offer
                                </Button>
                              ) : (
                                <Button
                                  variant="quiet"
                                  size="sm"
                                  onClick={() => setConfirm({ kind: "owner", person: p })}
                                >
                                  Offer to {firstName(p.name)}
                                </Button>
                              ))}
                            <Button variant="quiet" size="sm" onClick={() => setConfirm({ kind: "remove", person: p })}>
                              Remove
                            </Button>
                          </>
                        )}
                      </span>
                    )}
                    {notes.some((n) => n.on === p.member) && (
                      <div className="tm-person-notes">
                        <NoteList
                          notes={notes.filter((n) => n.on === p.member)}
                          members={trip.members}
                          you={trip.you}
                        />
                      </div>
                    )}
                    {pending?.kind === "remove" && (
                      <ConfirmRow
                        text={`Remove ${p.name}? They'll lose the trip. What they added stays.`}
                        action="Remove"
                        busy={busy}
                        onConfirm={() => act(() => api.remove(trip.id, member(p)))}
                        onCancel={() => setConfirm(null)}
                      />
                    )}
                    {pending?.kind === "owner" && (
                      <ConfirmRow
                        text={`Offer the trip to ${p.name}? It's theirs once they accept it, covering it as it is: with their plan, or a Trip Pass and spots if it needs them. Until then it stays yours.${
                          offeredTo
                            ? ` This takes back your offer to ${trip.members.find((m) => m.id === offeredTo.to)?.name ?? "someone else"}.`
                            : ""
                        }`}
                        action={`Offer to ${firstName(p.name)}`}
                        calm
                        busy={busy}
                        onConfirm={() => act(() => api.offerTrip(trip.id, member(p)))}
                        onCancel={() => setConfirm(null)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {(trip.former ?? []).length > 0 && (
            <section className="tm-people-block" aria-labelledby="former">
              <SectionTitle id="former" count={(trip.former ?? []).length}>
                Former members
              </SectionTitle>
              <ul className="tm-people-list">
                {(trip.former ?? []).map((p) => (
                  <li key={p.member ?? p.id} className="tm-person">
                    <FormerFace size={40} />
                    <span className="tm-person-name">
                      <span className="body-strong">A former member</span>
                      <span className="caption tm-muted">Deleted their account. What they added stays.</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {error && (
            <p className="callout tm-error" role="alert">
              {error}
              <ReportThis />
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

// Asked before it's done, on the person's row: in coral for what can't simply be taken back (removing someone, a new
// link); `calm` for what can (an offer of the trip, taken back with Withdraw offer).
function ConfirmRow(props: {
  text: string;
  action: string;
  busy: boolean;
  calm?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={`tm-confirm${props.calm ? " is-calm" : ""}`}>
      <p className="callout">{props.text}</p>
      <span className="tm-person-actions">
        <Button variant="quiet" size="sm" disabled={props.busy} onClick={props.onCancel}>
          Cancel
        </Button>
        {props.calm ? (
          <Button variant="primary" size="sm" disabled={props.busy} onClick={props.onConfirm}>
            {props.action}
          </Button>
        ) : (
          <Button variant="secondary" size="sm" className="tm-danger" disabled={props.busy} onClick={props.onConfirm}>
            {props.action}
          </Button>
        )}
      </span>
    </div>
  );
}

// Everyone's wants: Together, where the people picked agree and where they differ, and what I haven't said
// anything about.
function Wants({
  trip,
  items,
  notYet,
  onOpen,
  onStartRating,
}: {
  trip: TripView;
  items: Item[];
  // What waits on my rating, as the deck goes through it (a choice's options are rated on Bookings).
  notYet: Item[];
  onOpen: (id: string) => void;
  onStartRating: () => void;
}) {
  // Who's picked, kept on this device: everyone (null) until someone's left out, and everyone again once
  // they're all picked, so whoever joins later is picked too. Someone no longer in the trip is dropped.
  const [kept, setKept] = useKept<string[] | null>("people-picked", null);
  const [differOpen, setDifferOpen] = useKept("people-differ", false);
  // On a shared trip's view, only those whose ratings show: "A traveller" has none to compare.
  const viewing = useViewing();
  const members = viewing ? trip.members.filter((m) => !m.anonymous) : trip.members;
  const pickedIds = kept === null ? members.map((m) => m.id) : kept.filter((id) => members.some((m) => m.id === id));
  const picked = inOrder(members, trip.you).filter((m) => pickedIds.includes(m.id));
  const all = picked.length === members.length;
  const pick = (ids: string[]) => setKept(members.every((m) => ids.includes(m.id)) ? null : ids);
  const toggle = (id: string, on: boolean) =>
    pick(on ? [...pickedIds.filter((x) => x !== id), id] : pickedIds.filter((x) => x !== id));
  const meIn = picked.some((m) => m.id === trip.you);
  const one = picked.length === 1 ? picked[0] : undefined;
  const shared = together(items, picked);
  const agreed = TIERS.filter((t) => shared[t].length > 0);
  // The last thing opened from here, lit.
  const last = useLastOpened();
  const row = (i: Item, caption: ReactNode, faces: Person[] = []) => (
    <li key={i.id}>
      <button
        type="button"
        className={`tm-row${i.id === last ? ` is-lit ${kindClass(kindOf(i))}` : ""}`}
        {...litProps(i.id === last)}
        onClick={() => onOpen(i.id)}
      >
        <KindIcon item={i} />
        <span className="tm-row-text">
          <span className="callout">{i.title}</span>
          <span className="caption tm-muted">{caption}</span>
        </span>
        {faces.length > 0 && <AvatarStack people={faces} size={24} max={4} />}
      </button>
    </li>
  );
  const name = (m: Person) => (m.id === trip.you ? "you" : firstName(m.name));
  // The lists' headings: one person's tiers as they are; several people's, what they all say.
  // Said to the people picked: "you" when I'm one of them; "both" for two.
  const they = meIn ? "you" : "they";
  const them = meIn ? "you" : "them";
  const all2 = picked.length === 2 ? "both" : "all";
  const everyOne = picked.length === 2 ? "both" : "every one";
  const tierHead = (t: Tier) => (one ? TIER_LABEL[t] : `${picked.length === 2 ? "Both" : "All"} say ${TIER_LABEL[t]}`);
  return (
    <div className="tm-people-wants">
      <section className="tm-people-block" aria-labelledby="together">
        <h2 id="together" className="title">
          {viewing && members.length === 1 && members[0] ? `What ${firstName(members[0].name)} wants` : "Together"}
        </h2>
        {members.length > 1 && (
          <>
            <Whose members={members} you={trip.you} picked={pickedIds} onToggle={toggle} />
            <div className="tm-picked">
              <span className="caption tm-muted" aria-live="polite">
                {`${picked.length} picked`}
              </span>
              <Button
                variant="quiet"
                size="sm"
                className="tm-picked-all"
                onClick={() => pick(all ? [] : members.map((m) => m.id))}
              >
                {all ? "Clear" : "Pick everyone"}
              </Button>
            </div>
          </>
        )}
        {picked.length === 0 ? (
          <Empty>Nobody picked. Tap the faces of the people whose wants you'd like to see side by side.</Empty>
        ) : one && agreed.length === 0 ? (
          one.id === trip.you ? (
            <Empty prompt={examplePrompt(trip.title, "peopleMe")} readOnly="You haven't said what you want.">
              {notYet.length > 0
                ? "You haven't said what you want yet. Rate what's waiting for you below, or tell your AI what you'd love to do."
                : "You haven't said what you want yet. It shows here as you rate ideas, or as you tell your AI what you'd love to do."}
            </Empty>
          ) : (
            <Empty readOnly={`${firstName(one.name)} hasn't said what they want.`}>
              {firstName(one.name)} hasn't said what they want yet. It shows here as they rate ideas, here or through
              their AI.
            </Empty>
          )
        ) : agreed.length === 0 && shared.differ.length === 0 ? (
          // What to ask the AI: who hasn't said (only a read, so it stays on a read-only trip). Rating what I haven't
          // is the web's own (Not rated by you, with Start rating, below).
          <Empty
            prompt={examplePrompt(trip.title, "people")}
            promptReads
            readOnly={`Nothing ${they}'ve ${all2} rated. What ${everyOne} of ${them} rated shows here: where ${they} agree, and where ${they} differ.`}
          >
            {`Nothing ${they}'ve ${all2} rated yet. What ${everyOne} of ${them} rated shows here: where ${they} agree, and where ${they} differ.`}
          </Empty>
        ) : (
          <>
            {agreed.length === 0 && (
              <p className="callout tm-muted tm-empty">{`Nothing ${they} ${all2} say the same about yet.`}</p>
            )}
            {agreed.map((t) => (
              <section key={t} className="tm-tier" aria-label={tierHead(t)}>
                <h3 className="eyebrow tm-muted">
                  {tierHead(t)}
                  <Count n={shared[t].length} />
                </h3>
                <ul className="tm-rows">
                  {shared[t].map((i) => {
                    // Who else, of those not picked, says the same: wants it too (Yes or Maybe), or on the
                    // No list, says No too.
                    const same = (tier: string | null) =>
                      t === "no" ? tier === "no" : tier === "yes" || tier === "maybe";
                    const others = members.filter(
                      (m) => !pickedIds.includes(m.id) && i.interest.some((x) => x.person === m.id && same(x.tier)),
                    );
                    return row(
                      i,
                      others.length ? `${itemKind(i)} · ${others.map(name).join(", ")} too` : itemKind(i),
                      others,
                    );
                  })}
                </ul>
              </section>
            ))}
            {shared.differ.length > 0 && (
              <div className="tm-differ">
                <FoldLine open={differOpen} controls="people-differ" onToggle={() => setDifferOpen(!differOpen)}>
                  {`${shared.differ.length} where ${meIn ? "you" : "they"} differ`}
                </FoldLine>
                {differOpen && (
                  <ul id="people-differ" className="tm-rows">
                    {shared.differ.map(({ item, tiers }) => {
                      const groups = byTierGiven(tiers, members, trip.you);
                      const said = groups
                        .map((g) => `${TIER_LABEL[g.tier]} from ${listNames(g.people.map(name))}`)
                        .join(" · ");
                      return row(
                        item,
                        <span className="tm-differ-says" title={said}>
                          <span className="tm-hidden">{said}</span>
                          {groups.map((g) => (
                            <span key={g.tier} className="tm-differ-tier" aria-hidden="true">
                              <AvatarStack people={g.people} size={20} max={3} />
                              <span>{TIER_LABEL[g.tier]}</span>
                            </span>
                          ))}
                        </span>,
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {notYet.length > 0 && (
        <section className="tm-people-block" aria-labelledby="not-rated">
          <div className="tm-not-rated-head">
            <SectionTitle id="not-rated" count={notYet.length}>
              Not rated by you
            </SectionTitle>
            <Button variant="secondary" size="sm" icon="star" onClick={onStartRating}>
              Start rating
            </Button>
          </div>
          <p className="caption tm-muted">Rate them one at a time, or tell your AI how much you want each.</p>
          <ul className="tm-rows">{notYet.map((i) => row(i, wantsLine(i, trip.members, trip.you)))}</ul>
        </section>
      )}
    </div>
  );
}
